import React, { useContext, useEffect, useState } from 'react';
import TenantContext from '@/contexts/TenantContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Search, Plus, Phone, Mail, MapPin, MoreVertical, Edit2, Trash2, Star, ShieldCheck, Activity, Award, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ClientesCRMPage = () => {
  const { tenantSlug } = useContext(TenantContext);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  
  // Form State
  const [isEditing, setIsEditing] = useState(false);
  const [editingRecordId, setEditingRecordId] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    telefono: '',
    email: '',
    tipo: 'LEAD',
    estado: 'ACTIVO',
    direccion: '',
    rfc: '',
    notas: '',
    ltv: '0'
  });

  useEffect(() => {
    fetchClientes();
  }, [tenantSlug]);

  const fetchClientes = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/tenants/${tenantSlug}/engine/silos/clientes`);
      setClientes(res.data || []);
    } catch (err) {
      console.error(err);
      toast.error('Garantice que haya corrido el Activador de Kit 5x5 o creado el silo "clientes"');
      setClientes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.nombre || !formData.telefono) {
      toast.error("Nombre y Teléfono son obligatorios");
      return;
    }
    
    try {
      const payload = {
        data: {
          nombre: formData.nombre,
          telefono: formData.telefono,
          email: formData.email,
          tipo: formData.tipo,
          estado: formData.estado,
          direccion: formData.direccion,
          rfc: formData.rfc,
          notas: formData.notas,
          ltv: parseFloat(formData.ltv) || 0
        }
      };

      if (!isEditing) {
        payload.data.fecha_alta = new Date().toISOString();
        await axios.post(`${API_URL}/api/tenants/${tenantSlug}/engine/silos/clientes/inject`, payload);
        toast.success("Cliente inyectado al ecosistema");
      } else {
        await axios.put(`${API_URL}/api/tenants/${tenantSlug}/engine/silos/clientes/${editingRecordId}`, payload.data);
        toast.success("Registro de cliente actualizado");
      }

      setIsSidebarOpen(false);
      resetForm();
      fetchClientes();
    } catch (err) {
      toast.error(isEditing ? "Error al actualizar cliente" : "Error al inyectar cliente");
    }
  };

  const handleEdit = (cliente) => {
    setEditingRecordId(cliente._id);
    setIsEditing(true);
    setFormData({
      nombre: cliente.nombre || '',
      telefono: cliente.telefono || '',
      email: cliente.email || '',
      tipo: cliente.tipo || 'LEAD',
      estado: cliente.estado || 'ACTIVO',
      direccion: cliente.direccion || '',
      rfc: cliente.rfc || '',
      notas: cliente.notas || '',
      ltv: String(cliente.ltv || '0')
    });
    setIsSidebarOpen(true);
  };

  const resetForm = () => {
    setIsEditing(false);
    setEditingRecordId(null);
    setFormData({ 
      nombre: '', telefono: '', email: '', 
      tipo: 'LEAD', estado: 'ACTIVO', 
      direccion: '', rfc: '', notas: '', 
      ltv: '0' 
    });
  };

  const handleDelete = async (recordId) => {
    if(!window.confirm("¿Seguro que deseas eliminar este cliente?")) return;
    try {
      await axios.delete(`${API_URL}/api/tenants/${tenantSlug}/engine/silos/clientes/${recordId}`);
      toast.success("Cliente eliminado");
      fetchClientes();
    } catch (err) {
      toast.error("Error al eliminar cliente");
    }
  };

  const filteredClientes = clientes.filter(c => 
    c.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) || 
    c.telefono?.includes(searchTerm) ||
    c.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.rfc?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getInitials = (name) => {
    if(!name) return "X";
    const parts = name.split(' ');
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length-1][0]).toUpperCase();
    return name.substring(0, 2).toUpperCase();
  };

  const getTipoColor = (tipo) => {
    switch((tipo || '').toUpperCase()) {
      case 'VIP': return 'bg-yellow-500/20 text-yellow-500 border-yellow-500/50';
      case 'CORPORATIVO': return 'bg-purple-500/20 text-purple-400 border-purple-500/50';
      case 'FRECUENTE': return 'bg-emerald-500/20 text-emerald-500 border-emerald-500/50';
      case 'LEAD': return 'bg-blue-500/20 text-blue-400 border-blue-500/50';
      default: return 'bg-white/10 text-slate-300 border-white/20';
    }
  };

  return (
    <div className="h-full flex flex-col pt-8 lg:pt-0 pb-20 lg:pb-0">
      {/* Premium Header */}
      <div className="mb-8 relative">
        <div className="absolute inset-0 bg-indigo-500/10 blur-[100px] rounded-full pointer-events-none" />
        <div className="flex flex-col gap-6 relative z-10">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Award className="w-4 h-4 text-indigo-400" />
                <span className="text-[9px] uppercase tracking-[0.3em] font-black text-indigo-400">Hub Relacional</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-black tracking-tight text-white font-outfit leading-none">
                Fluxo <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-fuchsia-500">CRM</span>
              </h1>
              <p className="text-slate-400 mt-3 font-medium italic text-xs max-w-xl">Gestión inteligente de cartera y LTV impulsado por IA.</p>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
              className="flex items-center gap-3 bg-white/[0.03] border border-white/5 p-2 rounded-[1.5rem] w-full lg:w-auto lg:min-w-[500px]"
            >
                <div className="flex items-center bg-slate-900/50 rounded-xl border border-white/5 px-2">
                  <button 
                    onClick={() => document.getElementById('clientes-table-container').scrollBy({ left: -300, behavior: 'smooth' })}
                    className="p-2 text-slate-600 hover:text-white transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <div className="w-[1px] h-4 bg-white/5 mx-1" />
                  <button 
                    onClick={() => document.getElementById('clientes-table-container').scrollBy({ left: 300, behavior: 'smooth' })}
                    className="p-2 text-slate-600 hover:text-white transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>

                <div className="relative flex-1 group">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <input
                    type="text"
                    className="block w-full pl-9 pr-4 py-2.5 bg-slate-950/50 border border-white/10 rounded-xl text-xs placeholder-slate-600 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all font-bold tracking-tight"
                    placeholder="Buscar cliente..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                  
                <button 
                  onClick={() => { resetForm(); setIsSidebarOpen(true); }}
                  className="px-4 py-2.5 bg-gradient-to-r from-indigo-600 to-fuchsia-700 hover:from-indigo-500 hover:to-fuchsia-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shadow-lg hover:-translate-y-0.5 active:scale-95 flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  <span className="hidden sm:inline">Registrar</span>
                </button>
            </motion.div>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-slate-900 border border-white/5 rounded-[2rem] p-6 flex items-center gap-6 relative overflow-hidden group hover:border-indigo-500/30 transition-colors">
          <div className="absolute right-0 top-0 w-32 h-32 bg-indigo-500/5 rounded-full -translate-y-12 translate-x-12 blur-2xl group-hover:bg-indigo-500/10 transition-colors" />
          <div className="w-16 h-16 rounded-2xl bg-indigo-500/10 flex items-center justify-center border border-indigo-500/20">
            <Users className="w-8 h-8 text-indigo-400" />
          </div>
          <div>
             <h3 className="text-3xl font-black text-white">{clientes.length}</h3>
             <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mt-1">Cartera Activa</p>
          </div>
        </div>
        <div className="bg-slate-900 border border-white/5 rounded-[2rem] p-6 flex items-center gap-6 relative overflow-hidden group hover:border-fuchsia-500/30 transition-colors">
          <div className="absolute right-0 top-0 w-32 h-32 bg-fuchsia-500/5 rounded-full -translate-y-12 translate-x-12 blur-2xl group-hover:bg-fuchsia-500/10 transition-colors" />
          <div className="w-16 h-16 rounded-2xl bg-fuchsia-500/10 flex items-center justify-center border border-fuchsia-500/20">
            <ShieldCheck className="w-8 h-8 text-fuchsia-400" />
          </div>
          <div>
             <h3 className="text-3xl font-black text-white">{clientes.filter(c => ['VIP', 'CORPORATIVO'].includes((c.tipo || '').toUpperCase())).length}</h3>
             <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mt-1">Clientes Elite</p>
          </div>
        </div>
        <div className="bg-slate-900 border border-white/5 rounded-[2rem] p-6 flex items-center gap-6 relative overflow-hidden group hover:border-emerald-500/30 transition-colors">
          <div className="absolute right-0 top-0 w-32 h-32 bg-emerald-500/5 rounded-full -translate-y-12 translate-x-12 blur-2xl group-hover:bg-emerald-500/10 transition-colors" />
          <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 flex items-center justify-center border border-emerald-500/20">
            <Activity className="w-8 h-8 text-emerald-400" />
          </div>
          <div>
             <h3 className="text-3xl font-black text-white">${clientes.reduce((acc, c) => acc + parseFloat(c.ltv || 0), 0).toLocaleString()}</h3>
             <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mt-1">Valor Total (LTV)</p>
          </div>
        </div>
      </div>

      {/* Modern Table Layout */}
      <div className="bg-slate-900/50 backdrop-blur-xl border border-white/5 rounded-[2rem] flex-1 overflow-hidden flex flex-col">
          <div id="clientes-table-container" className="overflow-x-auto custom-scrollbar">
            <table className="w-full text-left border-collapse min-w-[1200px]">
              <thead>
                <tr className="bg-white/5 border-b border-white/5">
                  <th className="p-6 text-[11px] font-black uppercase tracking-[0.2em] text-slate-500 italic">Cliente / RFC</th>
                  <th className="p-6 text-[11px] font-black uppercase tracking-[0.2em] text-slate-500 italic">Contacto</th>
                  <th className="p-6 text-[11px] font-black uppercase tracking-[0.2em] text-slate-500 italic">Segmentación</th>
                  <th className="p-6 text-[11px] font-black uppercase tracking-[0.2em] text-slate-500 italic">Dirección</th>
                  <th className="p-6 text-[11px] font-black uppercase tracking-[0.2em] text-slate-500 italic">Valor (LTV)</th>
                  <th className="p-6 text-[11px] font-black uppercase tracking-[0.2em] text-slate-500 text-right italic">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {loading ? (
                   <tr>
                     <td colSpan="6" className="p-12 text-center text-slate-500 text-sm font-medium italic animate-pulse">Sincronizando Cerebro Relacional...</td>
                   </tr>
                ) : filteredClientes.length === 0 ? (
                   <tr>
                     <td colSpan="6" className="p-20 text-center">
                        <Users className="w-12 h-12 text-slate-700 mx-auto mb-4" />
                        <p className="text-lg text-slate-400 font-medium">Tu ecosistema CRM está vacío</p>
                        <p className="text-sm text-slate-500 mt-1 font-medium italic">Abre el panel lateral para inyectar tu primer cliente al flujo.</p>
                     </td>
                   </tr>
                ) : (
                   filteredClientes.map((cliente, index) => (
                    <motion.tr 
                      key={cliente._id || index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="hover:bg-indigo-500/[0.03] transition-all group border-b border-white/[0.03]"
                    >
                      <td className="p-6">
                        <div className="flex items-center gap-4">
                           <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-fuchsia-600 flex items-center justify-center text-white font-black text-lg shadow-lg border border-white/10 group-hover:scale-110 transition-transform">
                              {getInitials(cliente.nombre)}
                           </div>
                           <div>
                             <p className="text-white font-bold text-base leading-tight">{cliente.nombre || 'Sin Nombre'}</p>
                             <div className="flex items-center gap-2 mt-1">
                                <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">{cliente.rfc || 'SIN RFC'}</span>
                                <span className="w-1 h-1 rounded-full bg-slate-700" />
                                <span className="text-[10px] font-bold text-slate-600">ID: {cliente._id?.substring(0, 8)}</span>
                             </div>
                           </div>
                        </div>
                      </td>
                      <td className="p-6">
                        <div className="space-y-1.5">
                           <div className="flex items-center gap-2 text-slate-300 text-sm font-medium">
                             <Phone className="w-3.5 h-3.5 text-indigo-400" /> {cliente.telefono || 'N/A'}
                           </div>
                           {cliente.email && (
                             <div className="flex items-center gap-2 text-slate-400 text-xs font-medium">
                               <Mail className="w-3.5 h-3.5 text-slate-600" /> {cliente.email}
                             </div>
                           )}
                        </div>
                      </td>
                      <td className="p-6">
                        <span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border ${getTipoColor(cliente.tipo)}`}>
                          {cliente.tipo || 'LEAD'}
                        </span>
                      </td>
                      <td className="p-6">
                        <div className="flex items-start gap-2 max-w-[200px]">
                           <MapPin className="w-3.5 h-3.5 text-slate-600 mt-0.5 flex-shrink-0" />
                           <p className="text-xs text-slate-400 line-clamp-2 font-medium">{cliente.direccion || 'Sin dirección registrada'}</p>
                        </div>
                      </td>
                      <td className="p-6">
                         <div className="flex flex-col">
                            <div className="flex items-center gap-2">
                               <span className="text-emerald-400 font-black text-xl tracking-tighter italic">${parseFloat(cliente.ltv || 0).toLocaleString()}</span>
                               <span className="text-slate-600 font-black text-[9px] uppercase tracking-widest bg-white/5 px-2 py-0.5 rounded-md">MXN</span>
                            </div>
                            <span className="text-[9px] font-black text-slate-500 mt-1 uppercase tracking-[0.2em] italic">Lifetime Value</span>
                         </div>
                      </td>
                      <td className="p-6 text-right">
                          <div className="flex items-center justify-end gap-3">
                            <button 
                               onClick={() => handleEdit(cliente)}
                               className="p-3 bg-indigo-500/10 text-indigo-400 hover:bg-indigo-600 hover:text-white rounded-2xl transition-all opacity-0 group-hover:opacity-100 shadow-lg border border-indigo-500/20 active:scale-95"
                               title="Editar Perfil"
                             >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button 
                               onClick={() => handleDelete(cliente._id)}
                               className="p-3 bg-rose-500/10 text-rose-500/60 hover:bg-rose-600 hover:text-white rounded-2xl transition-all opacity-0 group-hover:opacity-100 border border-rose-500/20 active:scale-95"
                               title="Eliminar Cliente"
                             >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                      </td>
                    </motion.tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
      </div>

      {/* Right Sidebar Form for Data Inject */}
      <AnimatePresence>
        {isSidebarOpen && (
          <>
            <motion.div 
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-[#020617]/80 backdrop-blur-md z-[100]"
              onClick={() => setIsSidebarOpen(false)}
            />
            <motion.div 
              initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 28, stiffness: 220 }}
              className="fixed top-0 right-0 h-full w-full max-w-[500px] bg-[#0F172A] border-l border-white/10 z-[101] shadow-2xl flex flex-col overflow-hidden"
            >
              {/* Sidebar Header */}
              <div className="p-6 border-b border-white/5 bg-white/[0.02]">
                <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center border ${isEditing ? 'bg-amber-500/10 border-amber-500/20' : 'bg-indigo-500/10 border-indigo-500/20'}`}>
                            <Users className={`w-5 h-5 ${isEditing ? 'text-amber-400' : 'text-indigo-400'}`} />
                        </div>
                        <div>
                            <h2 className="text-xl font-black text-white tracking-tighter uppercase italic">{isEditing ? 'Modificar Perfil' : 'Registrar Cliente'}</h2>
                            <p className="text-slate-500 text-[9px] font-black uppercase tracking-widest mt-0.5 italic">Inteligencia Relacional Fluxo</p>
                        </div>
                    </div>
                    <button onClick={() => setIsSidebarOpen(false)} className="p-1.5 hover:bg-white/5 rounded-lg text-slate-500 transition-colors">
                      <Plus className="w-7 h-7 rotate-45" />
                    </button>
                </div>
              </div>

              {/* Sidebar Body */}
              <div className="flex-1 overflow-y-auto p-6 custom-scrollbar space-y-6">
                 {/* Basic Info Group */}
                 <div className="space-y-6">
                   <div className="flex items-center gap-3">
                      <div className="w-1 h-5 bg-indigo-500 rounded-full" />
                      <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 italic">Identificación & Fiscal</span>
                   </div>
                   
                   <div className="grid grid-cols-1 gap-6">
                     <div className="space-y-1.5">
                        <label className="text-[9px] font-black uppercase tracking-widest text-indigo-400 ml-1 italic">Nombre Completo *</label>
                        <div className="relative group">
                            <Users className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-indigo-500/50 transition-colors group-focus-within:text-indigo-400" />
                            <input 
                              type="text" 
                              className="w-full bg-indigo-500/[0.02] border border-indigo-500/20 rounded-xl py-2.5 pl-11 pr-4 text-white focus:border-indigo-500/50 outline-none transition-all font-black placeholder:text-slate-800 text-xs italic"
                              value={formData.nombre}
                              onChange={e => setFormData({...formData, nombre: e.target.value})}
                              placeholder="Ej. Elon Musk"
                            />
                        </div>
                     </div>
                     <div className="space-y-1.5">
                        <label className="text-[9px] font-black uppercase tracking-widest text-slate-500 ml-1 italic">RFC (ID Fiscal)</label>
                        <div className="relative group">
                            <ShieldCheck className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-600 transition-colors group-focus-within:text-indigo-400" />
                            <input 
                              type="text" 
                              className="w-full bg-black/40 border border-white/5 rounded-xl py-2.5 pl-11 pr-4 text-white focus:border-indigo-500/50 outline-none transition-all font-black placeholder:text-slate-800 uppercase italic tracking-widest text-xs"
                              value={formData.rfc}
                              onChange={e => setFormData({...formData, rfc: e.target.value.toUpperCase()})}
                              placeholder="XAXX010101000"
                            />
                        </div>
                     </div>
                   </div>
                 </div>

                 {/* Contact Group */}
                 <div className="space-y-6 pt-6 border-t border-white/5">
                   <div className="flex items-center gap-3">
                      <div className="w-1 h-5 bg-fuchsia-500 rounded-full" />
                      <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 italic">Canales de Contacto</span>
                   </div>
                   
                   <div className="grid grid-cols-1 gap-6">
                     <div className="space-y-1.5">
                        <label className="text-[9px] font-black uppercase tracking-widest text-fuchsia-400 ml-1 italic">WhatsApp / Móvil *</label>
                        <div className="relative group">
                            <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-fuchsia-500/50 transition-colors group-focus-within:text-fuchsia-400" />
                            <input 
                              type="text" 
                              className="w-full bg-fuchsia-500/[0.02] border border-fuchsia-500/20 rounded-xl py-2.5 pl-11 pr-4 text-white focus:border-fuchsia-500/50 outline-none transition-all font-black placeholder:text-slate-800 text-xs italic"
                              value={formData.telefono}
                              onChange={e => setFormData({...formData, telefono: e.target.value})}
                              placeholder="+52 1 55 ..."
                            />
                        </div>
                     </div>
                     <div className="space-y-1.5">
                        <label className="text-[9px] font-black uppercase tracking-widest text-slate-500 ml-1 italic">Email Principal</label>
                        <div className="relative group">
                            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-600 transition-colors group-focus-within:text-fuchsia-400" />
                            <input 
                              type="email" 
                              className="w-full bg-black/40 border border-white/5 rounded-xl py-2.5 pl-11 pr-4 text-white focus:border-indigo-500/50 outline-none transition-all font-black placeholder:text-slate-800 text-xs italic"
                              value={formData.email}
                              onChange={e => setFormData({...formData, email: e.target.value})}
                              placeholder="contacto@empresa.com"
                            />
                        </div>
                     </div>
                   </div>
                 </div>

                 {/* Categorization Group */}
                 <div className="space-y-6 pt-6 border-t border-white/5">
                   <div className="flex items-center gap-3">
                      <div className="w-1 h-5 bg-emerald-500 rounded-full" />
                      <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 italic">Segmentación & LTV</span>
                   </div>
                   
                   <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                          <label className="text-[9px] font-black uppercase tracking-widest text-slate-500 ml-1 italic">Segmento</label>
                          <select 
                              className="w-full bg-black/40 border border-white/5 rounded-xl py-2.5 px-3 text-white focus:border-indigo-500/50 outline-none transition-all appearance-none font-black text-[10px] uppercase tracking-widest italic"
                              value={formData.tipo}
                              onChange={e => setFormData({...formData, tipo: e.target.value})}
                          >
                              <option value="LEAD">Prospecto</option>
                              <option value="FRECUENTE">Frecuente</option>
                              <option value="VIP">VIP</option>
                              <option value="CORPORATIVO">Corp.</option>
                          </select>
                      </div>
                      <div className="space-y-1.5">
                          <label className="text-[9px] font-black uppercase tracking-widest text-emerald-400 ml-1 italic">Valor LTV ($)</label>
                          <input 
                            type="number" 
                            className="w-full bg-emerald-500/[0.02] border border-emerald-500/20 rounded-xl py-2.5 px-3 text-white focus:border-emerald-500/50 outline-none transition-all font-black placeholder:text-slate-800 italic text-xs"
                            value={formData.ltv}
                            onChange={e => setFormData({...formData, ltv: e.target.value})}
                            placeholder="0.00"
                          />
                      </div>
                   </div>
                 </div>

                 {/* Location & Intelligence Group */}
                 <div className="space-y-6 pt-6 border-t border-white/5 pb-4">
                   <div className="flex items-center gap-3">
                      <div className="w-1 h-5 bg-amber-500 rounded-full" />
                      <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 italic">Inteligencia & Contexto</span>
                   </div>
                   
                   <div className="space-y-1.5">
                      <label className="text-[9px] font-black uppercase tracking-widest text-slate-500 ml-1 italic">Dirección de Despacho</label>
                      <div className="relative group">
                        <MapPin className="absolute left-4 top-4 w-4 h-4 text-slate-600 transition-colors group-focus-within:text-amber-500" />
                        <textarea 
                            rows="2"
                            className="w-full bg-black/40 border border-white/5 rounded-xl py-2.5 pl-11 pr-4 text-white focus:border-indigo-500/50 outline-none transition-all font-medium placeholder:text-slate-800 resize-none text-xs italic"
                            value={formData.direccion}
                            onChange={e => setFormData({...formData, direccion: e.target.value})}
                            placeholder="Calle, Número, Colonia..."
                        />
                      </div>
                   </div>
                   <div className="space-y-1.5">
                      <label className="text-[9px] font-black uppercase tracking-widest text-amber-500 ml-1 italic">Notas IA</label>
                      <textarea 
                        rows="3"
                        className="w-full bg-amber-500/[0.02] border border-amber-500/10 rounded-xl p-3 text-white focus:border-amber-500/50 outline-none transition-all font-medium placeholder:text-slate-800 resize-none text-xs leading-relaxed italic"
                        value={formData.notas}
                        onChange={e => setFormData({...formData, notas: e.target.value})}
                        placeholder="Perfilado clave para el bot..."
                      />
                   </div>
                 </div>
              </div>

              {/* Sidebar Footer */}
              <div className="p-6 border-t border-white/5 bg-white/[0.02] flex items-center gap-4">
                  <div className="flex-1 p-3 rounded-xl bg-indigo-500/5 border border-indigo-500/10 flex items-center gap-3">
                      <Activity className="w-5 h-5 text-indigo-400" />
                      <div>
                          <p className="text-[8px] font-black text-indigo-400 uppercase tracking-widest leading-none">Status</p>
                          <p className="text-[9px] font-bold text-slate-500 uppercase tracking-tighter italic">Sincronizado</p>
                      </div>
                  </div>
                  <button 
                    onClick={handleSave}
                    className="flex-[2] py-3 bg-gradient-to-r from-indigo-600 to-fuchsia-700 hover:from-indigo-500 hover:to-fuchsia-600 text-white font-black uppercase tracking-[0.2em] text-[10px] rounded-xl shadow-[0_0_20px_rgba(99,102,241,0.2)] transition-all flex items-center justify-center gap-2 active:scale-95"
                  >
                    <Plus className="w-4 h-4" />
                    <span>{isEditing ? 'Actualizar' : 'Inyectar Registro'}</span>
                  </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ClientesCRMPage;
